import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Loading } from '../components/Loading';
import { EmptyState } from '../components/EmptyState';

// LE-040: convites de cadastro — o admin gera o código (válido 24h, uso único),
// copia e envia ao usuário pelo WhatsApp. O código aparece UMA vez na tela.
const STATUS_META = {
  pending: { label: 'Pendente', color: 'var(--nc-primary)', bg: 'var(--nc-success-soft)' },
  used: { label: 'Utilizado', color: 'var(--nc-text-secondary)', bg: 'var(--nc-surface-alt)' },
  revoked: { label: 'Cancelado', color: 'var(--nc-danger-text)', bg: 'var(--nc-danger-soft)' },
  locked: { label: 'Bloqueado', color: 'var(--nc-warning-text)', bg: 'var(--nc-warning-soft)' },
  expired: { label: 'Expirado', color: 'var(--nc-text-muted)', bg: '#f9fafb' },
};

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export function AdminInvitesPage() {
  const navigate = useNavigate();
  const [invites, setInvites] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [lastCode, setLastCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', phone: '' });

  const fetchInvites = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/invites', { params: { limit: 100 } });
      setInvites(data.data || []);
    } catch {
      setInvites([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { fetchInvites(); }, [fetchInvites]);

  const set = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    setCopied(false);
    try {
      const { data } = await api.post('/admin/invites', {
        email: form.email,
        name: form.name || undefined,
        phone: form.phone || undefined,
      });
      // Código em texto claro — exibido UMA vez (não fica em lugar nenhum depois)
      setLastCode(data.code);
      setMessage({ type: 'ok', text: 'Convite criado! Copie o código e envie ao usuário pelo WhatsApp.' });
      setForm({ email: '', name: '', phone: '' });
      fetchInvites();
    } catch (err) {
      setMessage({ type: 'err', text: err.response?.data?.error?.message || 'Erro ao criar convite.' });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!lastCode) return;
    try {
      await navigator.clipboard.writeText(lastCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback para navegadores sem clipboard API
      setCopied(false);
      setMessage({ type: 'err', text: `Não foi possível copiar automaticamente. Código: ${lastCode}` });
    }
  };

  const revokeInvite = async (invite) => {
    if (!window.confirm(`Cancelar o convite de ${invite.email}?`)) return;
    setMessage(null);
    try {
      await api.post(`/admin/invites/${invite.id}/revoke`);
      setMessage({ type: 'ok', text: `Convite de ${invite.email} cancelado.` });
      fetchInvites();
    } catch (err) {
      setMessage({ type: 'err', text: err.response?.data?.error?.message || 'Erro ao cancelar convite.' });
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid var(--nc-border)',
    fontSize: 14, marginBottom: 12, outline: 'none', background: 'var(--nc-surface)', color: 'var(--nc-text)', boxSizing: 'border-box'
  };
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--nc-text-secondary)', marginBottom: 6 };

  return (
    <div className="nc-page">
      <div className="nc-pagehead">
        <div className="nc-row">
          <button onClick={() => navigate('/admin')} aria-label="Voltar" className="nc-iconbtn">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="nc-page-title">Convites de cadastro</h1>
            <p style={{ fontSize: 11, opacity: 0.8, margin: '2px 0 0' }}>Código de uso único • válido por 24h</p>
          </div>
        </div>
      </div>

      <div style={{ padding: 16 }}>
        {message && (
          <div role="status" style={{
            padding: '10px 12px', borderRadius: 8, marginBottom: 12, fontSize: 13,
            background: message.type === 'ok' ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${message.type === 'ok' ? '#bbf7d0' : '#fecaca'}`,
            color: message.type === 'ok' ? '#15803d' : '#b91c1c'
          }}>
            {message.text}
          </div>
        )}

        {lastCode && (
          <div style={{
            background: 'var(--nc-warning-soft)', border: '1px solid var(--nc-warning-border)', borderRadius: 14, padding: 16, marginBottom: 18
          }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#92400e', marginBottom: 6 }}>
              🔑 Código de acesso — copie e envie ao usuário pelo WhatsApp (mostrado apenas uma vez)
            </div>
            <div style={{
              fontSize: 24, fontWeight: 700, letterSpacing: 2, fontFamily: 'monospace',
              color: 'var(--nc-text)', textAlign: 'center', padding: '10px 0', margin: '6px 0 10px'
            }}>
              {lastCode}
            </div>
            <button
              onClick={handleCopy}
              style={{
                width: '100%', padding: '12px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
                background: copied ? '#d1fae5' : '#0d5c3b', color: copied ? '#065f46' : '#fff',
                fontSize: 14, fontWeight: 600
              }}
            >
              {copied ? '✓ Copiado!' : 'Copiar código'}
            </button>
          </div>
        )}

        {/* Formulário */}
        <form onSubmit={handleCreate} style={{ background: 'var(--nc-surface)', borderRadius: 14, border: '1px solid var(--nc-border)', padding: 18, marginBottom: 18 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-text)', marginBottom: 12 }}>Gerar novo convite</div>
          <label style={labelStyle}>E-mail do usuário *</label>
          <input type="email" value={form.email} onChange={set('email')} required placeholder="usuario@exemplo.com" style={inputStyle} aria-label="E-mail do usuário" />
          <label style={labelStyle}>Nome (opcional — pré-preenche a confirmação)</label>
          <input type="text" value={form.name} onChange={set('name')} minLength={2} placeholder="Nome do usuário" style={inputStyle} aria-label="Nome do usuário" />
          <label style={labelStyle}>WhatsApp (opcional)</label>
          <input type="tel" value={form.phone} onChange={set('phone')} placeholder="(83) 99999-9999" style={inputStyle} aria-label="WhatsApp" />
          <button type="submit" disabled={loading} className="nc-btn-primary" style={{ width: '100%' }}>
            {loading ? 'Gerando...' : 'Gerar código'}
          </button>
        </form>

        {/* Lista */}
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
          Convites ({invites.length})
        </div>
        {loadingList ? <Loading /> : invites.length === 0 ? (
          <EmptyState icon="🔑" title="Nenhum convite" description="Gere o primeiro código de acesso acima." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {invites.map(inv => {
              const meta = STATUS_META[inv.status] || STATUS_META.pending;
              return (
                <div key={inv.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: 12,
                  background: 'var(--nc-surface)', borderRadius: 10, border: '1px solid var(--nc-border)'
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10, background: 'var(--nc-success-soft)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18
                  }}>🔑</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {inv.email}
                      {inv.name && <span className="nc-caption" style={{ fontWeight: 400, marginLeft: 6 }}>({inv.name})</span>}
                    </div>
                    <div className="nc-caption">
                      Expira em {formatDate(inv.expires_at)}{inv.used_at ? ` • usado em ${formatDate(inv.used_at)}` : ''}
                    </div>
                  </div>
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 999,
                    color: meta.color, background: meta.bg, whiteSpace: 'nowrap'
                  }}>
                    {meta.label}
                  </span>
                  {inv.status === 'pending' && (
                    <button
                      onClick={() => revokeInvite(inv)}
                      style={{
                        padding: '7px 12px', borderRadius: 8, border: '1px solid var(--nc-border)',
                        background: 'var(--nc-surface)', color: 'var(--nc-danger-text)', fontSize: 12, fontWeight: 500, cursor: 'pointer'
                      }}
                    >
                      Cancelar
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
